import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Activity, Loader2, Truck, Calendar as CalendarIcon, MapPin, Clock, Boxes } from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
    PieChart as RPieChart, Pie, Cell, Legend,
} from 'recharts';
import { useStatsBlockGuard } from '../hooks/useStatsBlockGuard';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PALETTE = ['#1B365D', '#C9A962', '#10B981', '#0EA5E9', '#8B5CF6', '#EF4444', '#F59E0B', '#14B8A6', '#EC4899', '#6366F1'];

export default function StatistiquesActivite() {
    useStatsBlockGuard('stats_activite');
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        axios.get(`${API_URL}/api/statistiques/activite-operationnelle`, { withCredentials: true })
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, []);

    const totals = data?.totals || {};
    const months = data?.months || [];
    const formules = data?.formules || [];
    const routes = data?.routes_top || [];

    const KPI = ({ label, value, icon: Icon, color, hint }) => (
        <Card className="shadow-sm">
            <CardContent className="p-5">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs uppercase text-gray-500 tracking-wide">{label}</p>
                        <p className="text-2xl font-bold text-[#1B365D] mt-1">{value}</p>
                        {hint ? <p className="text-xs text-gray-400 mt-0.5">{hint}</p> : null}
                    </div>
                    <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${color}1A` }}>
                        <Icon className="h-5 w-5" style={{ color }} />
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    return (
        <Layout>
            <div className="space-y-6 animate-fadeIn" data-testid="activite-page">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/statistiques')} className="h-8 w-8 p-0">
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div className="h-10 w-10 rounded-full bg-sky-100 flex items-center justify-center">
                        <Activity className="h-5 w-5 text-sky-500" />
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Activité opérationnelle</h1>
                        <p className="text-gray-500 mt-0.5 text-sm">Volumes, formules, destinations & équipe</p>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#1B365D]" /></div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <KPI label="Prestations 12m" value={totals.nb_prestations_12m || 0} icon={Truck} color="#1B365D" />
                            <KPI label="Volume m³" value={`${totals.volume_total_m3_12m || 0} m³`} icon={Boxes} color="#0EA5E9" />
                            <KPI label="Jours occupés" value={`${totals.jours_occupes_12m || 0} j`} icon={CalendarIcon} color="#10B981" hint="sur 12 mois" />
                            <KPI label="Heures équipe" value={`${totals.heures_pointage_30j || 0} h`} icon={Clock} color="#C9A962" hint="30 derniers jours" />
                        </div>

                        <Card className="shadow-sm">
                            <CardHeader className="pb-2"><CardTitle className="text-lg text-[#1B365D]">Prestations par mois</CardTitle></CardHeader>
                            <CardContent>
                                <div style={{ width: '100%', height: 280 }}>
                                    <ResponsiveContainer>
                                        <BarChart data={months}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                                            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                                            <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                                            <Tooltip />
                                            <Bar dataKey="count" name="Prestations" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <Card className="shadow-sm">
                                <CardHeader className="pb-2"><CardTitle className="text-lg text-[#1B365D]">Répartition des formules</CardTitle></CardHeader>
                                <CardContent>
                                    {formules.length === 0 ? (
                                        <p className="text-center text-gray-400 py-8">Aucune donnée</p>
                                    ) : (
                                        <div style={{ width: '100%', height: 280 }}>
                                            <ResponsiveContainer>
                                                <RPieChart>
                                                    <Pie data={formules} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={90} label={({ label, count }) => `${label} (${count})`}>
                                                        {formules.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                                                    </Pie>
                                                    <Tooltip />
                                                    <Legend />
                                                </RPieChart>
                                            </ResponsiveContainer>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            <Card className="shadow-sm">
                                <CardHeader className="pb-2"><CardTitle className="text-lg flex items-center gap-2 text-[#1B365D]"><MapPin className="w-5 h-5 text-rose-500" /> Top destinations</CardTitle></CardHeader>
                                <CardContent>
                                    {routes.length === 0 ? (
                                        <p className="text-center text-gray-400 py-8">Aucune donnée</p>
                                    ) : (
                                        <div className="divide-y">
                                            {routes.map((r, i) => (
                                                <div key={i} className="flex items-center justify-between py-2.5">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                                                        <span className="text-sm text-[#1B365D] truncate">{r.route}</span>
                                                    </div>
                                                    <span className="text-sm font-bold text-[#C9A962] shrink-0">×{r.count}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
}
